// Environment + header-tint helpers.
//
// The header bar is color-coded so you can never mistake which environment
// (and, in prod, which warehouse) you're looking at:
//   - local → amber
//   - dev   → green
//   - prod  → a per-warehouse color drawn from a fixed palette, assigned
//             deterministically from the warehouse code (so it's stable and
//             scales past the current warehouse count with no code changes).
//             "All Warehouses" / no selection → neutral (default header).
//
// Detection is BUILD-TIME (how the bundle was built), not hostname — so it
// stays correct under dev tunnels, LAN IPs, or any custom host. The three
// builds are distinguishable because:
//   local: `pnpm dev` (vite serve)            → import.meta.env.DEV === true
//   dev:   deploy.yml `vite build` + override → DEV false, VITE_DEV_FEATURES true
//   prod:  promote-prod `vite build`, no override → DEV false, VITE_DEV_FEATURES
//          falls back to .env.production's 'false'

export type AppEnvironment = 'local' | 'dev' | 'prod';

export function getEnvironment(): AppEnvironment {
  if (import.meta.env.DEV) return 'local';
  if (import.meta.env.VITE_DEV_FEATURES === 'true') return 'dev';
  return 'prod';
}

// Document/tab title per environment: prod is unadorned, dev/local are prefixed
// so a tab is never mistaken for prod.
const ENV_TITLE_PREFIX: Record<AppEnvironment, string> = {
  local: 'LOCAL ',
  dev: 'DEV ',
  prod: '',
};

/** The browser tab / document title for the current environment. */
export function getAppTitle(): string {
  return `${ENV_TITLE_PREFIX[getEnvironment()]}WMS Pro`;
}

// Favicon fill per environment — dev/local reuse the same hues as the header
// tints (green/amber) so the tab icon signals the environment at a glance;
// prod keeps the neutral brand blue (matches the static favicon.svg source).
export const ENV_FAVICON_HEX: Record<AppEnvironment, string> = {
  local: '#F59E0B', // amber-500
  dev: '#16A34A', // green-600
  prod: '#4E73A6', // brand blue
};

// Full Tailwind class strings (literal so the JIT compiler can see them).
// Just the bar fill (solid /95, /80 under backdrop-blur) — no border-color
// override, so the bar keeps the default subtle `border-border` bottom edge
// instead of a saturated darker line.
const HEADER_TINTS = {
  amber: 'bg-amber-500/95 supports-[backdrop-filter]:bg-amber-500/80',
  green: 'bg-green-600/95 supports-[backdrop-filter]:bg-green-600/80',
  blue: 'bg-blue-600/95 supports-[backdrop-filter]:bg-blue-600/80',
  violet: 'bg-violet-600/95 supports-[backdrop-filter]:bg-violet-600/80',
  rose: 'bg-rose-600/95 supports-[backdrop-filter]:bg-rose-600/80',
  cyan: 'bg-cyan-600/95 supports-[backdrop-filter]:bg-cyan-600/80',
  teal: 'bg-teal-600/95 supports-[backdrop-filter]:bg-teal-600/80',
  fuchsia: 'bg-fuchsia-600/95 supports-[backdrop-filter]:bg-fuchsia-600/80',
  indigo: 'bg-indigo-600/95 supports-[backdrop-filter]:bg-indigo-600/80',
  sky: 'bg-sky-600/95 supports-[backdrop-filter]:bg-sky-600/80',
} as const;

// Colors used to distinguish prod warehouses — deliberately excludes amber
// (local) and green (dev) so an environment is never confused for a warehouse.
const WAREHOUSE_PALETTE: Array<keyof typeof HEADER_TINTS> = [
  'blue',
  'violet',
  'rose',
  'cyan',
  'teal',
  'fuchsia',
  'indigo',
  'sky',
];

// Stable, order-independent string hash so a given warehouse code always maps
// to the same palette slot.
function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Tailwind classes to tint the header bar for the current environment.
 * Returns '' (no tint → default header) for prod with no specific warehouse
 * selected or "All Warehouses" mode.
 */
export function getHeaderTintClass(warehouseCode?: string | null): string {
  const env = getEnvironment();
  if (env === 'local') return HEADER_TINTS.amber;
  if (env === 'dev') return HEADER_TINTS.green;

  // prod: color by the active warehouse.
  const code = warehouseCode?.trim();
  if (!code || code === 'ALL') return '';
  const color = WAREHOUSE_PALETTE[hashCode(code) % WAREHOUSE_PALETTE.length];
  return HEADER_TINTS[color];
}

// Business-channel accent classes. The warehouse tint already owns the header
// *background* (getHeaderTintClass), and two full-bar backgrounds cannot
// coexist — so the channel reads as an ACCENT (a colored bottom border on the
// bar + a colored pill on the selector trigger) that stays legible over any
// warehouse background in both light and dark. Channels are data-driven (v2):
// each channel row stores a curated PALETTE KEY (not raw hex) and the FE maps
// that key → these pre-authored light/dark class strings. The keys are kept
// deliberately outside the WAREHOUSE_PALETTE hues so channel and warehouse
// accents are never confused; they mirror the backend's CHANNEL_PALETTE_KEYS.
const CHANNEL_PALETTE: Record<
  string,
  { bar: string; pill: string; dot: string }
> = {
  emerald: {
    bar: 'border-b-2 border-b-emerald-500 dark:border-b-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40',
    dot: 'bg-emerald-500',
  },
  orange: {
    bar: 'border-b-2 border-b-orange-500 dark:border-b-orange-400',
    pill: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-500/40',
    dot: 'bg-orange-500',
  },
  blue: {
    bar: 'border-b-2 border-b-blue-500 dark:border-b-blue-400',
    pill: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/40',
    dot: 'bg-blue-500',
  },
  violet: {
    bar: 'border-b-2 border-b-violet-500 dark:border-b-violet-400',
    pill: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/40',
    dot: 'bg-violet-500',
  },
  rose: {
    bar: 'border-b-2 border-b-rose-500 dark:border-b-rose-400',
    pill: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/40',
    dot: 'bg-rose-500',
  },
  amber: {
    bar: 'border-b-2 border-b-amber-500 dark:border-b-amber-400',
    pill: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40',
    dot: 'bg-amber-500',
  },
  teal: {
    bar: 'border-b-2 border-b-teal-500 dark:border-b-teal-400',
    pill: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-500/40',
    dot: 'bg-teal-500',
  },
  fuchsia: {
    bar: 'border-b-2 border-b-fuchsia-500 dark:border-b-fuchsia-400',
    pill: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border border-fuchsia-500/40',
    dot: 'bg-fuchsia-500',
  },
};

/** Curated channel palette keys — must match the backend CHANNEL_PALETTE_KEYS. */
export const CHANNEL_PALETTE_KEYS = Object.keys(
  CHANNEL_PALETTE,
) as ReadonlyArray<string>;

/**
 * Header-bar bottom-border accent for a channel palette key. '' for `ALL` /
 * missing / unknown keys (no channel filter → no accent), so it composes
 * cleanly with the warehouse tint.
 */
export function getChannelTintClass(colorKey?: string | null): string {
  if (!colorKey) return '';
  return CHANNEL_PALETTE[colorKey]?.bar ?? '';
}

/**
 * Colored pill classes for the channel selector trigger. '' for `ALL` /
 * missing / unknown keys.
 */
export function getChannelPillClass(colorKey?: string | null): string {
  if (!colorKey) return '';
  return CHANNEL_PALETTE[colorKey]?.pill ?? '';
}

/**
 * Solid swatch/dot class for a channel palette key (dropdown items, settings
 * list). '' for missing / unknown keys.
 */
export function getChannelDotClass(colorKey?: string | null): string {
  if (!colorKey) return '';
  return CHANNEL_PALETTE[colorKey]?.dot ?? '';
}
